import type { WebhookEndpoint } from '@compass/client/types/webhook';
import { AlertCircle, Plus, Webhook } from 'lucide-react';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import type { WebhookPermissions } from '@/hooks/useShouldShowWebhooksPage';
import { cn } from '@/lib/utils';
import { useStore } from '@/store';
import { getRelativeTime } from '@/utils/date';

import { useWebhookEndpointHealth } from '../hooks/useWebhookEndpointHealth';
import {
  EMPTY_VALUE,
  endpointDetailPath,
  eventTypeLabel,
  formatSuccessRate,
  isEndpointManaged,
  successRateTone,
  webhookErrorMessage,
} from '../utils';
import { type EndpointActionsController, EndpointActionsMenu } from './EndpointActions';
import { EndpointStatusBadge, EnvironmentBadge, ManagedEndpointBadge } from './StatusBadges';

interface EndpointsTabProps {
  organizationId: string;
  permissions: WebhookPermissions;
  endpoints: WebhookEndpoint[];
  isLoading: boolean;
  error: unknown;
  actions: EndpointActionsController;
}

const TONE_CLASS: Record<ReturnType<typeof successRateTone>, string> = {
  good: 'text-foreground',
  warn: 'text-attention-foreground',
  bad: 'text-destructive font-medium',
  none: 'text-muted-foreground',
};

/**
 * One row per endpoint with its 24-hour health. Health comes from a separate,
 * failure-tolerant query: if the delivery dispatcher is down those cells show
 * "—" and the table, menu and dialogs keep working.
 */
export function EndpointsTab({
  organizationId,
  permissions,
  endpoints,
  isLoading,
  error,
  actions,
}: EndpointsTabProps) {
  const { getTimeZone } = useStore();
  const timeZone = getTimeZone();
  const endpointIds = useMemo(
    () => endpoints.map(e => e.id).filter((id): id is string => !!id),
    [endpoints],
  );
  const health = useWebhookEndpointHealth(organizationId, endpointIds, permissions.canReadHealth);

  const addButton = permissions.canCreateEndpoint && (
    <Button size="sm" onClick={actions.openCreate} data-analytics-id="webhooks:endpoint:add">
      <Plus className="h-4 w-4 mr-2" />
      Add endpoint
    </Button>
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Failed to load endpoints</p>
            <p className="mt-1">{webhookErrorMessage(error, 'Please try again later.')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (endpoints.length === 0) {
    return (
      <div className="rounded-md border border-dashed bg-muted/30 px-6 py-10 text-center">
        <Webhook className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-sm font-medium">No endpoints yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Add an endpoint to receive signed case events as they happen.
        </p>
        {addButton && <div className="mt-4">{addButton}</div>}
      </div>
    );
  }

  const healthUnavailable = permissions.canReadHealth && health.isError;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {endpoints.length} {endpoints.length === 1 ? 'endpoint' : 'endpoints'}
          {healthUnavailable && ' · health is temporarily unavailable'}
        </p>
        {addButton}
      </div>
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Environment</TableHead>
              <TableHead>URL host</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Events</TableHead>
              <TableHead className="text-right">Success (24h)</TableHead>
              <TableHead>Last success</TableHead>
              <TableHead className="text-right">Consecutive failures</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {endpoints.map(endpoint => {
              const h = endpoint.id ? health.byId.get(endpoint.id) : undefined;
              const failures = h?.consecutive_failures ?? 0;
              const events = endpoint.event_types ?? [];
              return (
                <TableRow key={endpoint.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-wrap items-center gap-2">
                      {endpoint.id && permissions.canGetEndpoint ? (
                        <Link
                          to={endpointDetailPath(organizationId, endpoint.id)}
                          className="hover:underline"
                        >
                          {endpoint.name || endpoint.url_host || endpoint.id}
                        </Link>
                      ) : (
                        <span>{endpoint.name || endpoint.url_host || EMPTY_VALUE}</span>
                      )}
                      {isEndpointManaged(endpoint) && <ManagedEndpointBadge />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <EnvironmentBadge environment={endpoint.environment} />
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {endpoint.url_host || EMPTY_VALUE}
                  </TableCell>
                  <TableCell>
                    <EndpointStatusBadge status={endpoint.status} />
                  </TableCell>
                  <TableCell className="text-sm">
                    <SimpleTooltipV2 content={events.join(', ')}>
                      <span>
                        {events.length === 0
                          ? EMPTY_VALUE
                          : events.length === 1
                            ? eventTypeLabel(events[0])
                            : `${events.length} events`}
                      </span>
                    </SimpleTooltipV2>
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right tabular-nums',
                      TONE_CLASS[successRateTone(h?.success_rate)],
                    )}
                  >
                    {formatSuccessRate(h?.success_rate)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {h?.last_success_at
                      ? getRelativeTime(h.last_success_at, timeZone)
                      : EMPTY_VALUE}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right tabular-nums',
                      failures > 0 ? 'text-destructive font-medium' : 'text-muted-foreground',
                    )}
                  >
                    {h ? failures : EMPTY_VALUE}
                  </TableCell>
                  <TableCell className="text-right">
                    <EndpointActionsMenu
                      endpoint={endpoint}
                      permissions={permissions}
                      onAction={actions.run}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
